'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import type { Auth0IdTokenClaims } from '@libs/common-next';
import {
    AUTH0_LOYALTY_ID_CLAIM,
    AUTH0_REGISTRATION_CLAIM,
    isAuth0CallbackPath,
    useDecisions,
    useLoyaltyContext,
} from '@libs/common-next';

import { trackCreateAccount } from '../gtm/events';

const TRACKED_STORAGE_KEY_PREFIX = 'auth0:createAccountTracked:';

/** Mirrors the localStorage guard so private mode cannot double-fire within one page load. */
const trackedAccountIds = new Set<string>();

function trackedStorageKey(accountId: string) {
    return `${TRACKED_STORAGE_KEY_PREFIX}${accountId}`;
}

function hasTrackedCreateAccount(accountId: string): boolean {
    if (trackedAccountIds.has(accountId)) {
        return true;
    }

    try {
        return globalThis.localStorage.getItem(trackedStorageKey(accountId)) !== null;
    } catch {
        // Private mode or quota errors leave the in-memory guard as the only defence.
        return false;
    }
}

function markCreateAccountTracked(accountId: string) {
    trackedAccountIds.add(accountId);

    try {
        globalThis.localStorage.setItem(trackedStorageKey(accountId), 'true');
    } catch {
        // A failed write must not swallow the event, even though a later visit could then repeat it.
    }
}

/**
 * Fires GA4 `create_account` once per Auth0 registration, on the page the shopper lands on
 * after Universal Login.
 *
 * The signup intent that started the redirect is not proof: Universal Login lets the shopper
 * switch to login instead. The post-login Action confirms the registration through
 * `petsmart.com/registration`, so the id token is the only signal read here.
 *
 * Loyalty data is included when the provider already has it cached. A brand-new account
 * usually has none, and waiting for it would risk losing the event entirely — the login
 * event's loyalty wait is deliberately not reused.
 */
export function useAuth0CreateAccountEvent() {
    const isAuth0Enabled = useDecisions('auth0');
    const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();
    const { memberPoints } = useLoyaltyContext();
    const pathname = usePathname();

    // Read at fire time rather than depended on, so a late loyalty response neither delays
    // the event nor re-runs the effect once it arrives.
    const memberPointsRef = useRef(memberPoints);
    memberPointsRef.current = memberPoints;

    useEffect(() => {
        // `/auth/callback` holds the same authenticated session, but the event belongs to the
        // destination page — `Auth0TokenSync` document-navigates there once the cookie is written.
        if (!isAuth0Enabled || isLoading || !isAuthenticated || isAuth0CallbackPath(pathname ?? '')) {
            return;
        }

        let isMounted = true;

        async function trackNewRegistration() {
            let claims: Auth0IdTokenClaims | undefined;

            try {
                claims = (await getIdTokenClaims()) as Auth0IdTokenClaims | undefined;
            } catch {
                // An unreadable token is no confirmed registration.
                return;
            }

            if (!isMounted || claims?.[AUTH0_REGISTRATION_CLAIM] !== true) {
                return;
            }

            // The claim lives on the ID token, which Auth0 caches in localStorage. A sessionStorage
            // guard would clear on browser close while that token still reports a new registration.
            // `sub` is stable per account, so a later Auth0 session of the same user cannot convert twice.
            const accountId = claims.sub;
            if (!accountId || hasTrackedCreateAccount(accountId)) {
                return;
            }

            // Marked before the push, with nothing awaited in between, so a concurrent effect
            // run reads the guard back instead of pushing a second event.
            markCreateAccountTracked(accountId);

            trackCreateAccount({
                loyaltyId: claims[AUTH0_LOYALTY_ID_CLAIM],
                treatsPoints: memberPointsRef.current?.availablePoints,
                currentTierRank: memberPointsRef.current?.currentTierRank,
            });
        }

        void trackNewRegistration();

        return () => {
            isMounted = false;
        };
    }, [getIdTokenClaims, isAuth0Enabled, isAuthenticated, isLoading, pathname]);
}
